import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, CreditCard, Landmark, WifiOff } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiException, api } from '../../api/client'
import { useSession } from '../auth/useSession'
import { formatBytes } from '../../lib/format'
import { usePageTitle } from '../../lib/usePageTitle'
import { Banner, Button, Card, ErrorState, Field, Skeleton, cx } from '../../ui'
import type { CaptureSession } from '../../api/types.gen'
import { IzipayCardForm } from '../billing/IzipayCardForm'
import { TransferInstructionsCard } from '../billing/TransferInstructionsCard'
import type { BillingOverview, TransferInstructions } from '../billing/types'
import { formatSoles } from '../billing/types'

type Metodo = 'card' | 'transfer'

/**
 * Activación de la cuenta: el primer pago.
 *
 * Es la pantalla donde el producto pasa de existir a funcionar. Hasta que ese
 * pago entra, la cuenta está en `trialing` y no sube archivos — no hay capa
 * gratuita, así que cobrar es la condición para empezar (PLAN.md §12.5).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Dos métodos, y son asimétricos a propósito
 * ─────────────────────────────────────────────────────────────────────────────
 * Con TARJETA el cobro es síncrono y el resultado se ve aquí: si el banco
 * rechaza, el estudio tiene que enterarse con la tarjeta en la mano, no por un
 * correo al día siguiente cuando ya cree que tiene cuenta.
 *
 * Con TRANSFERENCIA no puede serlo: el dinero tarda hasta un día hábil y lo
 * confirma una persona (§12.8). La cuenta se queda en `trialing` hasta entonces,
 * y eso NO se suaviza — abrirle un hueco sería la capa gratuita por la puerta de
 * atrás. Lo que sí se hace es decirlo sin rodeos y acortar el bucle: el estudio
 * declara el pago en cuanto transfiere.
 *
 * La transferencia existe porque el pago recurrente con tarjeta empresarial es
 * minoritario en Perú (§12.4). Sin ella se pierde al fotógrafo individual
 * entero, que es justo el segmento de entrada.
 */
export function ActivatePage() {
  usePageTitle('Activa tu cuenta')
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const refreshTenant = useSession((state) => state.refreshTenant)
  const [metodo, setMetodo] = useState<Metodo | null>(null)
  const [token, setToken] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [instructions, setInstructions] = useState<TransferInstructions | null>(null)
  const [session, setSession] = useState<CaptureSession | null>(null)

  const overview = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => api.get<{ data: BillingOverview }>('/billing/subscription'),
  })

  // Compartido por los dos caminos de tarjeta —el formulario real y el token de
  // desarrollo—, porque lo que pasa DESPUÉS de cobrar es idéntico y tenerlo dos
  // veces es cómo uno de los dos se queda sin el `refreshTenant`.
  const alActivar = async () => {
    // El orden importa: primero se relee el tenant, después se navega. Al
    // revés, RequireActiveAccount lee el `trialing` viejo y devuelve aquí a
    // quien acaba de pagar.
    await refreshTenant()
    void queryClient.invalidateQueries({ queryKey: ['billing'] })
    void navigate('/projects', { replace: true })
  }

  const alFallar = (error: unknown) => {
    setFailure(
      error instanceof ApiException ? error.message : 'No se pudo procesar el pago. Inténtalo de nuevo.',
    )
  }

  const activate = useMutation({
    mutationFn: () =>
      api.post<{ data: { status: string; charged_cents: number } }>(
        '/billing/subscription/payment-method',
        { payment_token: token, card_brand: 'visa', card_last4: '4242' },
      ),
    onSuccess: alActivar,
    onError: alFallar,
  })

  /*
   | El permiso para pintar el formulario de la pasarela.
   |
   | Se pide al elegir «Tarjeta» y no al cargar la pantalla: es de un solo uso y
   | tiene caducidad corta, así que emitirlo para quien acabará pagando por
   | transferencia es gastarlo por nada y arriesgarse a que llegue vencido.
   */
  const abrirTarjeta = useMutation({
    mutationFn: () => api.post<{ data: CaptureSession }>('/billing/subscription/card-session', {}),
    onSuccess: (respuesta) => {
      setSession(respuesta.data)
      setMetodo('card')
    },
    onError: (error) => {
      setFailure(
        error instanceof ApiException
          ? error.message
          : 'No pudimos preparar el formulario de pago. Inténtalo de nuevo.',
      )
    },
  })

  /*
   | Lo que el formulario devolvió, de vuelta al servidor.
   |
   | Van las dos cadenas tal como llegaron. El token está dentro de `answer`,
   | pero de sacarlo se encarga el backend: la firma se calculó sobre ese texto
   | exacto, y dónde vive el token dentro es un detalle del proveedor que no
   | tiene por qué conocer una pantalla.
   */
  const activarConTarjeta = useMutation({
    mutationFn: ({ answer, hash }: { answer: string; hash: string }) =>
      api.post<{ data: { status: string; charged_cents: number } }>(
        '/billing/subscription/card-answer',
        { answer, hash },
      ),
    onSuccess: alActivar,
    onError: alFallar,
  })

  const abrirTransferencia = useMutation({
    mutationFn: () => api.post<{ data: TransferInstructions }>('/billing/subscription/transfer', {}),
    onSuccess: (respuesta) => {
      setInstructions(respuesta.data)
      setMetodo('transfer')
    },
    onError: (error) => {
      setFailure(
        error instanceof ApiException
          ? error.message
          : 'No pudimos preparar la transferencia. Inténtalo de nuevo.',
      )
    },
  })

  if (overview.isPending) {
    return (
      <section className="mx-auto flex max-w-form flex-col gap-6">
        <Skeleton className="h-9 w-52" />
        <Skeleton className="h-56 w-full rounded-card" />
      </section>
    )
  }

  if (overview.error) {
    return (
      <ErrorState
        icon={WifiOff}
        title="No se pudo cargar tu plan"
        description="Casi siempre es la conexión. Vuelve a intentarlo."
        action={
          <Button variant="secondary" onClick={() => void overview.refetch()}>
            Reintentar
          </Button>
        }
      />
    )
  }

  const { plan, subscription } = overview.data.data
  const reactivating = subscription.status !== 'trialing'

  return (
    <section className="mx-auto flex max-w-form flex-col gap-6">
      <div>
        <h1 className="text-title font-semibold text-ink">
          {reactivating ? 'Reactiva tu cuenta' : 'Ya casi está'}
        </h1>
        <p className="mt-1.5 text-body text-ink-muted">
          {reactivating
            ? 'En cuanto entre el pago, todo vuelve exactamente como estaba.'
            : 'Elige cómo quieres pagar y activamos la cuenta.'}
        </p>
      </div>

      {plan && (
        <Card className="p-5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-subhead font-medium text-ink">Plan {plan.name}</span>
            <span className="text-heading font-semibold text-ink" data-numeric>
              {formatSoles(plan.total_cents)}
            </span>
          </div>
          <p className="text-caption text-ink-subtle" data-numeric>
            {formatSoles(plan.price_net_cents)} + IGV, al mes
          </p>

          <ul className="mt-4 flex flex-col gap-1.5">
            {[
              `${formatBytes(plan.max_storage_bytes)} de almacenamiento`,
              `${plan.max_users} ${plan.max_users === 1 ? 'usuario' : 'usuarios'}`,
              'Descargas ilimitadas, sin coste por tráfico',
            ].map((feature) => (
              <li key={feature} className="flex items-start gap-2 text-body text-ink-muted">
                <Check
                  size={15}
                  strokeWidth={2.5}
                  aria-hidden
                  className="mt-1 shrink-0 text-positive"
                />
                {feature}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* El selector se queda visible: cambiar de idea a mitad no debe obligar a
          recargar, sobre todo con la transferencia, donde el estudio puede
          descubrir a media pantalla que prefiere la tarjeta. */}
      <fieldset className="flex flex-col gap-2">
        <legend className="sr-only">Cómo quieres pagar</legend>
        <div className="grid grid-cols-2 gap-2">
          <MetodoBoton
            icon={CreditCard}
            label="Tarjeta"
            hint="Al momento"
            seleccionado={metodo === 'card'}
            busy={abrirTarjeta.isPending}
            onClick={() => {
              setFailure(null)
              // El permiso ya pedido se reutiliza: cambiar de idea dos veces no
              // debe gastar uno nuevo ni tirar el formulario ya pintado.
              if (session) setMetodo('card')
              else abrirTarjeta.mutate()
            }}
          />
          <MetodoBoton
            icon={Landmark}
            label="Transferencia"
            hint="Mismo día hábil"
            seleccionado={metodo === 'transfer'}
            busy={abrirTransferencia.isPending}
            onClick={() => {
              setFailure(null)
              // Idempotente en el servidor: si ya hay una proforma viva devuelve
              // ésa, así que volver aquí no genera una referencia nueva.
              if (instructions) setMetodo('transfer')
              else abrirTransferencia.mutate()
            }}
          />
        </div>
      </fieldset>

      {failure && (
        <Banner tone="danger" title="No se pudo continuar" live="alert">
          {failure}
        </Banner>
      )}

      {metodo === 'card' && session && (
        <>
          {/*
            Dos formularios posibles, y lo que decide es si el servidor ha dado
            un sitio de dónde cargarlo. La pantalla NO pregunta qué pasarela hay
            configurada: `endpoint` vacío significa «no hay formulario que
            pintar», que es lo que devuelve la pasarela falsa, y así el camino de
            desarrollo sigue funcionando sin credenciales de nadie.
          */}
          {session.endpoint === '' ? (
            <div className="rounded-card border border-dashed border-warning-line bg-warning-subtle p-4">
              <p className="text-micro font-semibold uppercase tracking-wide text-warning">
                Sólo desarrollo
              </p>
              <p className="mt-1 text-body text-ink-muted">
                Sin pasarela configurada. Con un token de prueba:{' '}
                <code className="rounded bg-surface-sunken px-1 py-0.5 text-label">tok_ok</code>{' '}
                cobra,{' '}
                <code className="rounded bg-surface-sunken px-1 py-0.5 text-label">tok_hard</code> lo
                rechaza.
              </p>
              <Field
                label="Token de prueba"
                className="mt-3"
                value={token}
                onChange={(event) => setToken(event.target.value)}
                placeholder="tok_ok"
              />
            </div>
          ) : (
            <IzipayCardForm
              session={session}
              onCaptured={(answer, hash) => {
                setFailure(null)
                activarConTarjeta.mutate({ answer, hash })
              }}
              onFailure={setFailure}
            />
          )}

          {plan && (
            <p className="text-body text-ink-muted">
              Se cobrará{' '}
              <strong className="font-semibold text-ink" data-numeric>
                {formatSoles(plan.total_cents)}
              </strong>{' '}
              ahora, y después cada mes. Puedes cancelar cuando quieras.
            </p>
          )}

          {/*
            El botón es sólo del camino de desarrollo. El formulario de Izipay
            trae el suyo dentro —lo pinta la pasarela, con los campos— y poner
            otro al lado daría dos botones de pagar en la misma pantalla.
          */}
          {session.endpoint === '' ? (
            <Button
              size="lg"
              block
              disabled={token.trim() === ''}
              busy={activate.isPending && 'Procesando el pago…'}
              onClick={() => {
                setFailure(null)
                activate.mutate()
              }}
            >
              {plan ? `Pagar ${formatSoles(plan.total_cents)} y activar` : 'Activar'}
            </Button>
          ) : (
            activarConTarjeta.isPending && (
              /*
               | Entre que la pasarela acepta la tarjeta y que nuestro cobro
               | responde pasan segundos, y el botón que lo lanzó es de Izipay:
               | no podemos ponerlo en «procesando». Sin este aviso la pantalla
               | se queda quieta justo después de meter una tarjeta, que es el
               | momento en el que la gente vuelve a pulsar.
               */
              <Banner tone="accent" title="Procesando el pago…" live="status">
                No cierres esta ventana. Estamos cobrando y activando tu cuenta.
              </Banner>
            )
          )}
        </>
      )}

      {metodo === 'transfer' && instructions && (
        <>
          {/* Se dice antes de las cuentas, no después: quien elige transferencia
              tiene que saber que la cuenta no se activa en el acto ANTES de
              ponerse a copiar un CCI. */}
          <Banner tone="warning" title="La cuenta se activa cuando confirmemos el pago">
            No es inmediato: una transferencia tarda hasta un día hábil y la revisamos a mano.
            Mientras tanto no puedes subir archivos. Avísanos en cuanto transfieras y lo miramos el
            mismo día.
          </Banner>

          <TransferInstructionsCard
            instructions={instructions}
            onDeclared={() =>
              setInstructions((previas) => (previas ? { ...previas, declared: true } : previas))
            }
          />
        </>
      )}
    </section>
  )
}

function MetodoBoton({
  icon: Icon,
  label,
  hint,
  seleccionado,
  busy = false,
  onClick,
}: {
  icon: typeof CreditCard
  label: string
  hint: string
  seleccionado: boolean
  busy?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={seleccionado}
      disabled={busy}
      onClick={onClick}
      className={cx(
        'flex flex-col items-start gap-1 rounded-card border p-4 text-left transition-colors',
        seleccionado
          ? 'border-accent bg-accent-subtle text-accent'
          : 'border-line text-ink hover:border-ink-subtle',
      )}
    >
      <Icon size={22} strokeWidth={1.75} aria-hidden />
      <span className="text-label font-medium">{label}</span>
      <span className="text-caption text-ink-subtle">{busy ? 'Preparando…' : hint}</span>
    </button>
  )
}
